import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ChevronDown, TrendingUp, MapPinned, Boxes, Sparkles, LifeBuoy, Rocket, Check } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { CloudBlob, MaskLogo } from "@/components/Mask";
import { FRANCHISE_ADVANTAGES, INVESTMENT, FRANCHISE_FAQ } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADV_ICONS = [TrendingUp, Sparkles, MapPinned, Boxes, LifeBuoy, Rocket];

const SUPPORT = [
  "Site selection & feasibility",
  "Store design & kitchen setup",
  "Staff hiring & training",
  "Central supply of sauces & key ingredients",
  "Launch & ongoing marketing",
  "Operations & quality playbooks",
];

const empty = {
  name: "", phone: "", email: "", city: "", state: "",
  investment_budget: "", preferred_location: "", business_experience: "", message: "",
};

function Field({ label, name, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-koreyaah-indigo">{label}{required && <span className="text-koreyaah-red"> *</span>}</span>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required}
        data-testid={testid} placeholder={placeholder}
        className="w-full rounded-2xl border-2 border-koreyaah-indigo/15 bg-koreyaah-ghost px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red"
      />
    </label>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-koreyaah-indigo/15">
      <button data-testid={`faq-toggle-${idx}`} onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-4 py-6 text-left">
        <span className="font-display text-lg md:text-xl font-semibold text-koreyaah-indigo">{q}</span>
        <ChevronDown className={`shrink-0 text-koreyaah-red transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
            <p className="pb-6 text-koreyaah-indigo/75 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Franchise() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/franchise-inquiries`, form);
      toast.success("Enquiry received! Our team will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="franchise-page" className="pt-28">
      {/* Hero */}
      <section className="relative overflow-hidden mx-auto max-w-7xl px-5 py-16 md:py-24">
        <CloudBlob className="pointer-events-none absolute -right-6 top-6 h-44 w-80 opacity-10" color="#EF1C25" />
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Franchise with us</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 max-w-4xl font-display text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tight text-koreyaah-indigo leading-[0.9]">
            Own a slice of <span className="text-koreyaah-red">Seoul</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}><p className="mt-8 max-w-2xl text-lg md:text-xl text-koreyaah-indigo/80">Korean food is India's fastest-rising cuisine. Partner with Koreyaah and bring the buzz of Seoul street food to your city — with a proven model and full support.</p></Reveal>
        <Reveal delay={0.15}>
          <a href="#inquiry" data-testid="franchise-hero-cta" className="mt-8 inline-flex rounded-full bg-koreyaah-red px-8 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1">Enquire Now</a>
        </Reveal>
      </section>

      {/* Investment highlights */}
      <section className="bg-koreyaah-cobalt py-16 text-koreyaah-cream">
        <div className="mx-auto max-w-7xl px-5 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {INVESTMENT.map((it, i) => (
            <Reveal key={it.label} delay={i * 0.06}>
              <div className="text-center sm:text-left">
                <p className="font-display text-4xl md:text-5xl font-bold text-koreyaah-red">{it.value}</p>
                <p className="mt-2 text-sm uppercase tracking-widest text-koreyaah-cream/70">{it.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-7xl px-5 text-xs text-koreyaah-cream/50">*Figures are indicative and vary by city, format and site. Confirmed after evaluation.</p>
      </section>

      {/* Why invest / advantages */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:py-28">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Why Invest</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-3 max-w-3xl font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo leading-[0.95]">A brand built to scale</h2></Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FRANCHISE_ADVANTAGES.map((a, i) => {
            const Icon = ADV_ICONS[i % ADV_ICONS.length];
            return (
              <Reveal key={a.title} delay={(i % 3) * 0.06}>
                <div className="h-full rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-1">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-koreyaah-red text-white"><Icon size={26} /></div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-koreyaah-indigo">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-koreyaah-indigo/70">{a.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Support + Expansion vision */}
      <section className="bg-koreyaah-ghost py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Support Offered</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight text-koreyaah-indigo">We've got your back</h2>
            <ul className="mt-8 space-y-4">
              {SUPPORT.map((s) => (
                <li key={s} className="flex items-center gap-3 text-koreyaah-indigo/80">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-koreyaah-red text-white"><Check size={16} /></span>{s}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2.5rem] bg-koreyaah-red p-10 md:p-12 text-koreyaah-cream">
              <MaskLogo className="h-14 w-14" variant="white" />
              <p className="mt-6 text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-cream/70">Expansion Vision</p>
              <p className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-tight">From Bangalore to every craving in India.</p>
              <p className="mt-4 text-koreyaah-cream/85 leading-relaxed">Starting in Bangalore and growing through franchise partners — our vision is to make Korean street food an everyday delight across the country's fastest-growing markets.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-20 md:py-28">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">FAQ</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight text-koreyaah-indigo">Questions, answered</h2></Reveal>
        <div className="mt-10">
          {FRANCHISE_FAQ.map((f, i) => <FaqItem key={i} idx={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* Inquiry form */}
      <section id="inquiry" className="bg-koreyaah-cobalt py-20 md:py-28 text-koreyaah-cream">
        <div className="mx-auto max-w-3xl px-5">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Franchise Enquiry</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight">Let's build together</h2>
          <p className="mt-4 text-koreyaah-cream/80">Fill in your details and our franchise team will get in touch.</p>

          <form onSubmit={submit} data-testid="franchise-form" className="mt-10 rounded-[2.5rem] bg-koreyaah-cream p-6 md:p-10">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="franchise-input-name" placeholder="Your name" />
              <Field label="Phone Number" name="phone" value={form.phone} onChange={onChange} required testid="franchise-input-phone" placeholder="+91 ..." />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="franchise-input-email" placeholder="you@email.com" />
              <Field label="City" name="city" value={form.city} onChange={onChange} required testid="franchise-input-city" placeholder="City" />
              <Field label="State" name="state" value={form.state} onChange={onChange} required testid="franchise-input-state" placeholder="State" />
              <Field label="Investment Budget" name="investment_budget" value={form.investment_budget} onChange={onChange} required testid="franchise-input-budget" placeholder="e.g. ₹25–45 L" />
              <Field label="Preferred Location" name="preferred_location" value={form.preferred_location} onChange={onChange} required testid="franchise-input-location" placeholder="Area / mall / high-street" />
              <Field label="Business Experience" name="business_experience" value={form.business_experience} onChange={onChange} testid="franchise-input-experience" placeholder="Optional" />
            </div>
            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-koreyaah-indigo">Message</span>
              <textarea name="message" value={form.message} onChange={onChange} rows={4} data-testid="franchise-input-message" placeholder="Tell us about your goals..." className="w-full rounded-2xl border-2 border-koreyaah-indigo/15 bg-koreyaah-ghost px-4 py-3 text-koreyaah-indigo outline-none transition-colors duration-200 focus:border-koreyaah-red" />
            </label>
            <button type="submit" disabled={loading} data-testid="franchise-submit" className="mt-6 w-full rounded-full bg-koreyaah-red px-8 py-4 text-base font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-koreyaah-red-deep disabled:opacity-60">
              {loading ? "Submitting..." : "Submit Enquiry"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
